import React, { useState, useEffect, useRef } from 'react'
import { X, Plus, Trash2, Edit2 } from 'lucide-react'
import axiosInstance from '../../../services/axiosInstance'
import DeleteModal from '../DeleteModal'
import './styles.scss'

export default function DocumentRulesModal({ isOpen, onClose, program, ruleToEdit, nextDisplayOrder }) {
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Default values for a new rule
  const defaultRule = {
    documentType: '',
    description: '',
    instructions: '',
    required: true,
    active: true,
    displayOrder: nextDisplayOrder || 0,
    maxAgeDays: '',
    allowedFileTypes: []
  }
  
  const [formData, setFormData] = useState({ ...defaultRule })

  useEffect(() => {
    if (isOpen && program) {
      if (ruleToEdit) {
        setFormData({ ...ruleToEdit })
      } else {
        setFormData({ ...defaultRule, displayOrder: nextDisplayOrder || 0 })
      }
      setError('')
      setSuccess('')
    }
  }, [isOpen, program, ruleToEdit, nextDisplayOrder])

  const handleSaveRule = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    if (!formData.documentType || !formData.description) {
      setError('Document Type and Description are required.')
      return
    }
    
    setIsSubmitting(true)
    try {
      if (ruleToEdit) {
        await axiosInstance.put(`/api/settings/document-rules/${ruleToEdit.id}`, formData)
      } else {
        await axiosInstance.post(`/api/settings/programs/${program.id}/document-rules`, formData)
      }
      setSuccess(ruleToEdit ? 'Rule updated successfully.' : 'Rule added successfully.')
      setTimeout(() => {
        setSuccess('')
        onClose()
      }, 1000)
    } catch (err) {
      setError('Failed to save rule.')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const cancelEdit = () => {
    setEditingRule(null)
    setFormData({ ...defaultRule })
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content fade-in" style={{ maxWidth: '800px', width: '90%' }}>
        <div className="modal-header">
          <div>
            <h2>{ruleToEdit ? 'Edit Document Rule' : 'Add Document Rule'}</h2>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.9rem' }}>
              For Program: {program?.name}
            </p>
          </div>
          <button className="btn-close" onClick={onClose}><X size={20}/></button>
        </div>
        
        <div className="modal-body" style={{ padding: '24px', overflowY: 'auto', maxHeight: '70vh' }}>
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          
          <form onSubmit={handleSaveRule} className="modal-form">
            <div className="form-group">
              <label>Document Type Code</label>
              <select 
                required
                value={formData.documentType}
                onChange={e => setFormData({...formData, documentType: e.target.value})}
                disabled={!!ruleToEdit}
                className="form-control"
                style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%', marginBottom: '8px', fontSize: '14px' }}
              >
                <option value="" disabled>Select a Document Type</option>
                <option value="passport">Passport</option>
                <option value="transcript">Transcript</option>
                <option value="english_proficiency">English Proficiency</option>
                <option value="statement_of_purpose">Statement of Purpose</option>
                <option value="recommendation_letters">Recommendation Letters</option>
                <option value="financial_documents">Financial Documents</option>
                <option value="resume">Resume / CV</option>
              </select>
              <small className="text-muted">Must be selected from the predefined list.</small>
            </div>
            
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label>Display Name / Description</label>
              <input 
                type="text" 
                required
                placeholder="e.g. Official Undergraduate Transcripts"
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
                className="form-control"
                style={{ padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '100%' }}
              />
            </div>
            
            <div className="form-group">
              <label>Instructions for Student</label>
              <textarea 
                rows={4}
                placeholder="Detailed instructions on what to upload..."
                value={formData.instructions || ''}
                onChange={e => setFormData({...formData, instructions: e.target.value})}
              />
            </div>
            
            <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: '8px', marginBottom: '1rem' }}>
              <input 
                type="checkbox" 
                id="ruleRequired"
                checked={formData.required}
                onChange={e => setFormData({...formData, required: e.target.checked})}
                style={{ width: 'auto', height: 'auto', margin: 0, padding: 0 }}
              />
              <label htmlFor="ruleRequired" style={{ margin: 0, fontWeight: 'normal' }}>This document is required to submit the application</label>
            </div>
            
            <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
              <button type="button" className="btn btn-outline" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Rule'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
